import { useSelector } from 'react-redux';
import { getSheetsState } from '../sheetSelectors';
import { useEffect, useRef } from 'react';
import { isEmpty, omit } from 'lodash';
import { SheetReducerState } from '../sheetTypes';
import usePrevious from '../../../../utils/react/usePrevious';
import { SheetProps } from '../SheetContainer';
import { getSheetSnapPointWindowHeight } from '../utils/sheetHeightUtils';
import useUpdatedRef from '../../../../utils/react/useUpdatedRef';

export default (sheetProps: SheetProps) => {
    const { sheetRef, sheetKey, isSheetOpen, activeSnapPoint, snapPoints, dispatchUpdateActiveSnapPoint } = sheetProps;

    const sheetsState = useSelector(getSheetsState);
    const prevSheetsState = usePrevious(sheetsState);

    const activeSnapPointRef = useUpdatedRef(activeSnapPoint);
    const returnSnapPoint = useRef<number | null>(null);

    /**
     * On obstruction added (e.g. keyboard shown), adjust the snap points to accommodate the obstruction
     */
    const onObstructionAdded = (height: { px: number } | { point: number }) => {
        const maxSnapPoint = Math.max(...(snapPoints || []), activeSnapPointRef.current);

        // If max snap point >=90% of the window height, adjust the active snap point to
        // that value to account for the obstruction (e.g. keyboard).
        if (maxSnapPoint && maxSnapPoint >= 0.9) {
            dispatchUpdateActiveSnapPoint(maxSnapPoint);
            return;
        }

        // Otherwise, calculate new snap point that will push the fixed height of sheet content
        // to above the obstruction (maxed at 1 snap point).

        if (!sheetRef.current) return;

        const obstructionHeightPoint = 'point' in height ? height.point : height.px / getSheetSnapPointWindowHeight();
        const temporarySnapPoint = Math.min(obstructionHeightPoint + activeSnapPointRef.current, 1);

        returnSnapPoint.current = activeSnapPointRef.current;
        dispatchUpdateActiveSnapPoint(temporarySnapPoint);
    };

    /**
     * On obstruction removed (e.g. keyboard hide), reset the snap points to default state if needed
     */
    const onObstructionRemoved = () => {
        if (!returnSnapPoint.current) return;

        // If the sheet is fixed height, reset the active snap point to the default fixed height

        dispatchUpdateActiveSnapPoint(returnSnapPoint.current);
        returnSnapPoint.current = null;
    };

    /**
     * When another supporting sheet is opened, expand the current sheet to its max snap point
     */
    useEffect(() => {
        if (!isSheetOpen) return;

        const otherOpenSheets: SheetReducerState = omit(sheetsState, sheetKey);
        const prevOtherOpenSheets: SheetReducerState = omit(prevSheetsState, sheetKey);
        const otherSheetsJustOpened = isEmpty(prevOtherOpenSheets) && !isEmpty(otherOpenSheets);
        if (!otherSheetsJustOpened) return;

        const currentSheetContainsEditor = !!sheetsState[sheetKey]?.config?.containsElementEditor;
        if (!currentSheetContainsEditor) return;

        // Get the highest active snap point from other open sheets as the obstruction height point
        const obstructionHeightPoint = Object.values(otherOpenSheets).reduce((acc, sheet) => {
            if (sheet && sheet.activeSnapPoint > acc) return sheet.activeSnapPoint;
            return acc;
        }, 0);

        onObstructionAdded({ point: obstructionHeightPoint });

        return () => {
            onObstructionRemoved();
        };
    }, [sheetsState]);

    /**
     * When the keyboard is shown, expand the sheet to its max snap point
     */
    useEffect(() => {
        if (!isSheetOpen) return;

        // @ts-ignore - This is a custom event with no type definition
        const onKeyboardWillShow = (event: Event) => onObstructionAdded({ px: event.detail?.keyboardHeight || 0 });
        const onKeyboardWillHide = () => onObstructionRemoved();

        window.addEventListener('keyboardWillShow', onKeyboardWillShow);
        window.addEventListener('keyboardWillHide', onKeyboardWillHide);

        return () => {
            window.removeEventListener('keyboardWillShow', onKeyboardWillShow);
            window.removeEventListener('keyboardWillHide', onKeyboardWillHide);
        };
    }, [isSheetOpen]);
};
